import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { langfuseApi } from '@/api/langfuse.api';
import { Trace } from '@/proto/agentplatform/v1/langfuse_pb';
import { ListTracesRequestSchema } from '@/proto/agentplatform/httpv2/observability_pb';
import { create } from '@bufbuild/protobuf';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ChevronLeftIcon, ChevronRightIcon } from '@/components/icons';
import { TraceDetailsDialog } from '@/components/TraceDetailsDialog';
import { formatTimestamp } from '@/utils/trace.utils';

export default function AgentTrace() {
  const { id: agentId } = useParams<{ id: string }>();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selectedTraceId, setSelectedTraceId] = useState<string | null>(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);

  const { data, isLoading, error, isFetching } = useQuery({
    queryKey: ['agent-traces', agentId, page, pageSize],
    queryFn: async () => {
      const tags: string[] = [];
      if (agentId) {
        tags.push(`agent=${agentId}`);
      }
      const request = create(ListTracesRequestSchema, {
        tags,
        page,
        pageSize,
      });
      return langfuseApi.listTraces(request);
    },
    enabled: !!agentId,
  });

  const traces = data?.data ?? [];
  const totalCount = data?.total ?? 0;
  const totalPages = data?.totalPages ?? 1;

  const handleTraceClick = (traceId: string) => {
    setSelectedTraceId(traceId);
    setDetailsDialogOpen(true);
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        {/* <div>
          <h1 className="text-2xl font-bold tracking-tight">Trace</h1>
          <p className="text-muted-foreground mt-1">Agent 调用链路追踪</p>
        </div> */}
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center mx-auto mb-4">
                <svg
                  className="w-8 h-8 text-destructive"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-semibold mb-2">加载失败</h3>
              <p className="text-muted-foreground mb-4">无法加载 Trace 数据</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* <div>
        <h1 className="text-2xl font-bold tracking-tight">Trace</h1>
        <p className="text-muted-foreground mt-1">Agent 调用链路追踪</p>
      </div> */}

      <Card>
        <CardHeader>
          <CardTitle>调用记录</CardTitle>
          <CardDescription>共 {totalCount} 条记录</CardDescription>
        </CardHeader>
        <CardContent>
          {traces.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <svg
                className="w-16 h-16 mx-auto mb-4 opacity-50"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.5}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2"
                />
              </svg>
              <p className="text-lg font-medium mb-2">暂无调用记录</p>
              <p className="text-sm">调用记录将在 Agent 被使用时显示</p>
            </div>
          ) : (
            <>
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50 border-b">
                    <tr>
                      <th className="px-3 py-3 text-left text-xs font-medium text-muted-foreground">
                        Trace ID
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-muted-foreground">
                        时间
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-muted-foreground">
                        环境
                      </th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-muted-foreground">
                        标签
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {traces.map((trace: Trace) => (
                      <tr
                        key={trace.id}
                        className="hover:bg-muted/30 cursor-pointer"
                        onClick={() => handleTraceClick(trace.id)}
                      >
                        <td className="px-3 py-3 font-mono text-xs max-w-[150px] truncate">
                          {trace.id}
                        </td>
                        <td className="px-3 py-3 text-muted-foreground">
                          {formatTimestamp(trace.timestamp)}
                        </td>
                        <td className="px-3 py-3">
                          {trace.environment ? (
                            <Badge variant="outline" className="text-xs">
                              {trace.environment}
                            </Badge>
                          ) : (
                            '-'
                          )}
                        </td>
                        <td className="px-3 py-3">
                          <div className="flex flex-wrap gap-1">
                            {trace.tags.slice(0, 2).map((tag: string) => (
                              <Badge key={tag} variant="outline" className="text-xs">
                                {tag}
                              </Badge>
                            ))}
                            {trace.tags.length > 2 && (
                              <Badge variant="outline" className="text-xs">
                                +{trace.tags.length - 2}
                              </Badge>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex items-center justify-between mt-4">
                <div className="flex items-center gap-4">
                  <p className="text-sm text-muted-foreground">
                    第 {page} / {totalPages || 1} 页，共 {totalCount} 条
                  </p>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">每页</span>
                    <select
                      value={pageSize}
                      onChange={(e) => {
                        setPageSize(Number(e.target.value));
                        setPage(1);
                      }}
                      className="h-8 rounded-md border border-input bg-transparent px-2 py-1 text-sm hover:bg-accent hover:text-accent-foreground"
                    >
                      <option value={1}>1</option>
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                    <span className="text-sm text-muted-foreground">条</span>
                  </div>
                </div>
                {totalPages > 1 && (
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page <= 1 || isFetching}
                    >
                      <ChevronLeftIcon className="h-4 w-4 mr-1" />
                      上一页
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                      disabled={page >= totalPages || isFetching}
                    >
                      下一页
                      <ChevronRightIcon className="h-4 w-4 ml-1" />
                    </Button>
                  </div>
                )}
              </div>
            </>
          )}
        </CardContent>
      </Card>
      <TraceDetailsDialog
        traceId={selectedTraceId}
        open={detailsDialogOpen}
        onOpenChange={setDetailsDialogOpen}
      />
    </div>
  );
}
